import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getAccount } from '../services/accountService';
import { getWorkspaces } from '../services/appService';
import { authSignIn, authSignOut, autoLogin, forgotPasswordSubmit, sendForgottenCode } from '../services/auth';
import { accountState, isAdminState } from '../storage/account';
import { isLoggedInState, workspacesState } from '../storage/app';
import { initialCenteringState } from '../storage/map';
import { useClearStorage } from './useClearStorage';
import { useFeedback } from './useFeedback';

export function useAuth() {
  const setAccount = useSetRecoilState(accountState);
  const isAdmin = useRecoilValue(isAdminState);
  const setWorkspaces = useSetRecoilState(workspacesState);
  const setLoggedIn = useSetRecoilState(isLoggedInState);
  const clearStorage = useClearStorage();
  const { showFailFeedback } = useFeedback();
  const navigate = useNavigate();
  const setInitialCentering = useSetRecoilState(initialCenteringState);

  const login = async (username: string, password: string) => {
    const { error } = await authSignIn(username, password);
    if (!error) {
      await initApp();
    } else {
      return { error };
    }
  };

  const initApp = async () => {
    const { user: isLoggedIn, error } = await autoLogin();
    if (isLoggedIn && !error) {
      setLoggedIn(true);
      const { account, error } = await getAccount();
      if (!error && account) {
        setAccount(account);
        if (!isAdmin && account.client) {
          const { workspaces, error } = await getWorkspaces(account.client._id);
          if (!error && workspaces) {
            setWorkspaces(workspaces);
          } else {
            showFailFeedback(
              error ||
                'Ocurrió un error al cargar tus espacios de trabajo, inicia sesión de nuevo o inténtalo más tarde.'
            );
            navigate('500', { replace: false });
          }
        }
      } else {
        logout();
      }
    } else {
      logout();
    }
  };

  const logout = async () => {
    await authSignOut();
    clearStorage();
    setLoggedIn(false);
    setInitialCentering(true);
  };

  const sendCode = async (username: string) => {
    const { user, error } = await sendForgottenCode(username);
    if (!error) {
      return { user };
    } else {
      return { error };
    }
  };

  const resetForgotPassword = async (username: string, code: string, password: string) => {
    const { user, error } = await forgotPasswordSubmit(username, code, password);
    if (!error) {
      return { user };
    } else {
      return { error };
    }
  };

  return {
    login,
    logout,
    initApp,
    sendCode,
    resetForgotPassword,
  };
}
